import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../app/context/AuthContext";
import {
  getUserProfile,
  updateUserProfile,
} from "../../../core/services/keycloakApi";
import {
  deleteProfilePhoto,
  getProfile,
  uploadProfile,
} from "../../../core/services/profile.service";
import { extractApiErrorMessage } from "../../../core/utils/apiErrorUtils";
import { isAdminUser } from "../../../core/utils/util";

export const useProfile = () => {
  const { userInfo } = useAuth();
  const userId = userInfo?.id;
  const profileImageUrlRef = useRef("");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileFile, setProfileFile] = useState(null);
  const [profilePreview, setProfilePreview] = useState("");
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [profileUpload, setProfileUpload] = useState(null);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [deletingProfile, setDeletingProfile] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
  });
  const [tab, setTab] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const revokeProfileImageUrl = useCallback(() => {
    if (profileImageUrlRef.current) {
      URL.revokeObjectURL(profileImageUrlRef.current);
      profileImageUrlRef.current = "";
    }
  }, []);

  const loadProfileImage = useCallback(async () => {
    try {
      const res = await getProfile();
      const blob = res?.data;

      if (!(blob instanceof Blob) || blob.size === 0) {
        return false;
      }

      const objectUrl = URL.createObjectURL(blob);
      revokeProfileImageUrl();
      profileImageUrlRef.current = objectUrl;
      setProfileImageUrl(objectUrl);
      return true;
    } catch {
      return false;
    }
  }, [revokeProfileImageUrl]);

  useEffect(() => {
    return () => revokeProfileImageUrl();
  }, [revokeProfileImageUrl]);

  useEffect(() => {
    if (!userId) {
      revokeProfileImageUrl();
      setProfileImageUrl("");
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      try {
        const res = await getUserProfile(userId);
        const u = res?.data?.data;

        if (!u) throw new Error("Invalid user response");

        const fullName = `${u.firstName || ""} ${u.lastName || ""}`.trim();

        setUser({
          ...u,
          name: fullName,
          joinedOn: u.createdTimestamp,
        });

        setFormData({
          firstName: u.firstName || "",
          lastName: u.lastName || "",
          username: u.username || "",
          email: u.email || "",
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: "Failed to load profile",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    revokeProfileImageUrl();
    setProfileImageUrl("");
    loadProfileImage();
  }, [loadProfileImage, revokeProfileImageUrl, userId]);

  useEffect(() => {
    if (!profileFile) {
      setProfilePreview("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(profileFile);
    setProfilePreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [profileFile]);

  const handleSave = async () => {
    if (!userId) return;

    const firstName = formData.firstName.trim();
    const lastName = formData.lastName.trim();
    const username = formData.username.trim();
    const fullName = `${firstName} ${lastName}`.trim();

    const payload = {
      firstName,
      lastName,
      username,
      email: formData.email,
    };

    try {
      const res = await updateUserProfile(userId, payload);

      if (res?.data?.status === "success" || res.status === 200) {
        setUser((prev) => ({
          ...prev,
          name: fullName,
          email: formData.email,
          firstName,
          lastName,
          username,
        }));

        setTab(0);

        setSnackbar({
          open: true,
          message: "Profile updated successfully!",
          severity: "success",
        });
      } else {
        throw new Error("Update failed");
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Update failed",
        severity: "error",
      });
    }
  };

  const handleTabChange = (_, newValue) => {
    setTab(newValue);
  };

  const handleFormDataChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleProfileFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isImageMimeType = file.type?.startsWith("image/");
    const isGifFile = /\.gif$/i.test(file.name || "");

    if (!isImageMimeType && !isGifFile) {
      setSnackbar({
        open: true,
        message: "Please choose an image file, including GIF.",
        severity: "error",
      });
      event.target.value = "";
      return;
    }

    setProfileFile(file);
    event.target.value = "";
  };

  const handleProfileUpload = async () => {
    if (!profileFile) {
      setSnackbar({
        open: true,
        message: "Please choose a profile image first.",
        severity: "warning",
      });
      return;
    }

    setUploadingProfile(true);

    try {
      const res = await uploadProfile(profileFile);
      const uploadedProfile = res?.data;

      setProfileUpload(uploadedProfile);
      setUser((prev) => ({
        ...prev,
        profilePicture: uploadedProfile,
      }));

      const imageLoaded = await loadProfileImage();
      if (imageLoaded) {
        setProfileFile(null);
        window.dispatchEvent(new Event("profile-image-updated"));
      }

      setSnackbar({
        open: true,
        message: "Profile picture uploaded successfully!",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: extractApiErrorMessage(
          error.response?.data,
          error.message || "Profile picture upload failed",
        ),
        severity: "error",
      });
    } finally {
      setUploadingProfile(false);
    }
  };

  const handleProfileDelete = async () => {
    setDeletingProfile(true);

    try {
      await deleteProfilePhoto();

      revokeProfileImageUrl();
      setProfileImageUrl("");
      setProfileFile(null);
      setProfileUpload(null);
      setUser((prev) => ({
        ...prev,
        profilePicture: null,
      }));
      window.dispatchEvent(new Event("profile-image-updated"));

      setSnackbar({
        open: true,
        message: "Profile picture deleted successfully!",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: extractApiErrorMessage(
          error.response?.data,
          error.message || "Profile picture delete failed",
        ),
        severity: "error",
      });
    } finally {
      setDeletingProfile(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const roleLabel = userInfo?.roleLabel || "User";
  const isAdmin = isAdminUser(userInfo);
  const avatarSrc = profilePreview || profileImageUrl;
  const hasProfileImage = Boolean(profileImageUrl);

  return {
    user,
    loading,
    profileFile,
    profilePreview,
    profileImageUrl,
    avatarSrc,
    profileUpload,
    uploadingProfile,
    deletingProfile,
    formData,
    tab,
    snackbar,
    isAdmin,
    roleLabel,
    hasProfileImage,
    handleSave,
    handleTabChange,
    handleFormDataChange,
    handleProfileFileChange,
    handleProfileUpload,
    handleProfileDelete,
    handleSnackbarClose,
  };
};
